import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MapPin, ArrowLeft, Loader2 } from 'lucide-react';
import { getExplorer, getFileUrl } from '@/lib/api';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'hier';
  const days = Math.floor(diff / 86400);
  if (days < 7) return `il y a ${days}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const createHeartIcon = (isVerified) => {
  const color = isVerified ? '#E63946' : '#FFFFFF';
  return L.divIcon({
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `<svg viewBox="0 0 24 24" width="28" height="28" fill="${color}" style="filter: drop-shadow(0 0 6px ${isVerified ? 'rgba(230,57,70,0.7)' : 'rgba(255,255,255,0.5)'})"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`,
  });
};

const FitBoundsToMarkers = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length === 0) return;
    const bounds = L.latLngBounds(coords.map(h => [h.latitude, h.longitude]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [coords, map]);
  return null;
};

const ExplorerProfile = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getExplorer(deviceId);
        setData(result);
      } catch (e) {
        setError("Explorateur non trouvé");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [deviceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6">
        <Heart className="w-16 h-16 text-[#27272A] mb-4" />
        <p className="text-white/40">{error}</p>
        <button onClick={() => navigate('/leaderboard')} className="text-[#E63946] text-sm mt-4">Retour au classement</button>
      </div>
    );
  }

  const { pseudo, hearts_found, rank, total_explorers, hearts } = data;
  const heartsWithCoords = hearts.filter(h => h.latitude && h.longitude);
  const points = data.points || hearts.reduce((sum, h) => sum + (h.heart_number || 0), 0);

  const rankSuffix = rank === 1 ? 'er' : 'e';

  return (
    <div className="pb-4 overflow-y-auto" style={{ height: 'calc(100dvh - 56px - 60px)' }} data-testid="explorer-profile-page">

      {/* Back button */}
      <button
        onClick={() => navigate('/leaderboard')}
        className="absolute top-[70px] left-4 z-20 w-9 h-9 flex items-center justify-center rounded-full"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
        data-testid="back-to-leaderboard"
      >
        <ArrowLeft className="w-4 h-4 text-white" />
      </button>

      {/* Hero header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center pt-8 pb-6 px-6"
        style={{ background: 'linear-gradient(to bottom, rgba(230,57,70,0.08), transparent)' }}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E63946]/15 border border-[#E63946]/30 rounded-sm mb-4">
          <span className="text-[#E63946] text-xs font-bold uppercase tracking-wider">{rank}{rankSuffix} Finder</span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white uppercase mb-2" data-testid="explorer-pseudo">
          {pseudo}
        </h1>

        <div className="flex items-center justify-center gap-8 mt-4">
          <div className="text-center">
            <div className="text-3xl font-display font-bold text-white">{points}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">points</div>
          </div>
          <div className="w-px h-10 bg-[#27272A]" />
          <div className="text-center">
            <div className="text-3xl font-display font-bold text-[#E63946]">{hearts_found}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">cœurs</div>
          </div>
        </div>
      </motion.div>

      {/* Mini map */}
      {heartsWithCoords.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mx-4 mb-5 overflow-hidden border border-[#27272A]"
          style={{ height: '200px' }}
        >
          <MapContainer
            center={[48.8566, 2.3522]}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            attributionControl={false}
            dragging={false}
            scrollWheelZoom={false}
            touchZoom={false}
            doubleClickZoom={false}
          >
            <FitBoundsToMarkers coords={heartsWithCoords} />
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="dark-tiles" />
            {heartsWithCoords.map(h => (
              <Marker
                key={h.heart_number}
                position={[h.latitude, h.longitude]}
                icon={createHeartIcon(h.verification_status === 'verified')}
              >
                <Popup>
                  <span style={{ color: '#E63946', fontWeight: 'bold' }}>#{h.heart_number}</span>
                  <br />{h.location_name || 'Paris'}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </motion.div>
      )}

      {/* Photo gallery grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="px-4 mb-5"
      >
        <p className="text-[10px] uppercase tracking-widest text-[#52525B] font-semibold mb-2">Ses découvertes</p>
        <div className="grid grid-cols-3 gap-1.5">
          {hearts.map(h => (
            <button
              key={h.heart_number}
              onClick={() => navigate(`/heart/${h.heart_number}`)}
              className="relative aspect-square bg-[#141416] border border-[#1D1D21] overflow-hidden group"
              data-testid={`explorer-heart-${h.heart_number}`}
            >
              {h.close_photo_path ? (
                <img
                  src={getFileUrl(h.close_photo_path)}
                  alt={`#${h.heart_number}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#E63946]/30" />
                </div>
              )}
              {/* Number overlay */}
              <div className="absolute bottom-0 left-0 right-0 py-1 px-1.5 flex items-center justify-between"
                style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }}>
                <span className="text-white font-bold text-[11px]">#{h.heart_number}</span>
                {h.verification_status === 'verified' && (
                  <Heart className="w-2.5 h-2.5 text-[#E63946]" fill="#E63946" />
                )}
              </div>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="px-4"
      >
        <p className="text-[10px] uppercase tracking-widest text-[#52525B] font-semibold mb-2">Chronologie</p>
        <div className="space-y-1">
          {hearts.map(h => {
            const isVerified = h.verification_status === 'verified';
            return (
              <button
                key={h.heart_number}
                onClick={() => navigate(`/heart/${h.heart_number}`)}
                className="w-full flex items-center gap-2.5 px-3 py-2 bg-white/[0.06] backdrop-blur-sm border-l-2 border-[#E63946]/60 hover:border-[#E63946] hover:bg-white/[0.1] transition-all text-left rounded-r-sm"
              >
                {h.close_photo_path ? (
                  <div className="w-8 h-8 bg-[#27272A] overflow-hidden flex-shrink-0 rounded-sm">
                    <img src={getFileUrl(h.close_photo_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-white/[0.06] flex items-center justify-center flex-shrink-0 rounded-sm">
                    <Heart className={`w-3 h-3 ${isVerified ? 'text-[#E63946]' : 'text-white'}`} fill={isVerified ? '#E63946' : 'white'} />
                  </div>
                )}
                <span className={`font-bold text-sm w-10 flex-shrink-0 ${isVerified ? 'text-[#ff6b7a]' : 'text-white'}`}>#{h.heart_number}</span>
                <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm flex-shrink-0 ${isVerified ? 'bg-[#E63946]/20 text-[#E63946]' : 'bg-white/10 text-white/60'}`}>
                  {isVerified ? 'Vérifié' : 'Déclaré'}
                </span>
                <div className="flex-1 min-w-0 text-[11px] text-white/75 truncate flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5 flex-shrink-0 text-white/45" />{h.location_name || 'Paris'}
                </div>
                <span className="text-[10px] text-white/40 flex-shrink-0 whitespace-nowrap">{timeAgo(h.discovered_at)}</span>
              </button>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default ExplorerProfile;
